// =============================================================================
// components/QuickSearch.js — Global Hızlı Arama (Faz AY)
// =============================================================================
// Cmd+K (Ctrl+K Windows) ile açılır spotlight dialog.
// /api/admin/quick-search'den orders + customers + products gelir.
// Klavye: ↑↓ navigation, Enter aç, Esc kapat
// =============================================================================

import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, TextField, List, ListItemButton,
  ListItemText, Typography, Box, Chip, Divider, InputAdornment, CircularProgress
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PersonIcon from "@mui/icons-material/Person";
import InventoryIcon from "@mui/icons-material/Inventory";

const API_BASE = "https://api.miksanplastik.com.tr";

export default function QuickSearch() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ orders: [], customers: [], products: [] });
  const [activeIdx, setActiveIdx] = useState(0);
  const debounceRef = useRef(null);

  // Cmd+K / Ctrl+K global shortcut
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Debounced search (300ms)
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 2) {
      setResults({ orders: [], customers: [], products: [] });
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_BASE}/api/admin/quick-search?q=${encodeURIComponent(q)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setResults(data);
          setActiveIdx(0);
        }
      } catch (e) {
        // silent — kullanıcı dialog'u kapatabilir
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [q, open]);

  // Düz liste — keyboard navigation için
  const flatItems = [
    ...results.orders.map(o => ({ type: "order", id: o.id, primary: `#${o.id} — ${o.user_name || "İsimsiz"}`, secondary: `${parseFloat(o.total_price || 0).toFixed(2)} ₺ · ${o.status}`, path: `/orders` })),
    ...results.customers.map(c => ({ type: "customer", id: c.id, primary: c.company_name || c.name, secondary: `${c.email || c.phone || ""} ${c.tax_number ? "· VKN " + c.tax_number : ""}`, path: `/users` })),
    ...results.products.map(p => ({ type: "product", id: p.id, primary: p.name, secondary: `${p.sku || ""} · Stok: ${p.stock}`, path: `/products` }))
  ];

  const handleSelect = useCallback((item) => {
    if (!item) return;
    setOpen(false);
    setQ("");
    navigate(item.path);
  }, [navigate]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, flatItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleSelect(flatItems[activeIdx]);
    }
  };

  const renderGroup = (title, items, Icon, startOffset) => {
    if (items.length === 0) return null;
    return (
      <Box>
        <Typography variant="caption" sx={{ px: 2, py: 1, display: "block", color: "text.secondary", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>
          {title}
        </Typography>
        <List dense disablePadding>
          {items.map((item, i) => {
            const idx = startOffset + i;
            return (
              <ListItemButton
                key={`${item.type}-${item.id}`}
                selected={idx === activeIdx}
                onClick={() => handleSelect(item)}
                sx={{ px: 2 }}
              >
                <Icon fontSize="small" sx={{ mr: 2, color: "text.secondary" }} />
                <ListItemText primary={item.primary} secondary={item.secondary} />
                <Chip label={item.type} size="small" variant="outlined" />
              </ListItemButton>
            );
          })}
        </List>
        <Divider />
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={() => { setOpen(false); setQ(""); }}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { mt: 4, borderRadius: 2, alignSelf: "flex-start" } }}
    >
      <DialogContent sx={{ p: 0 }}>
        <TextField
          autoFocus
          fullWidth
          placeholder="Sipariş #, müşteri, ürün ara… (Esc kapat)"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            endAdornment: loading ? <CircularProgress size={18} /> : null,
            sx: { fontSize: 18, py: 1, px: 2 }
          }}
          variant="standard"
          sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}
        />
        <Box sx={{ maxHeight: 480, overflow: "auto" }}>
          {q.trim().length < 2 && (
            <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
              En az 2 karakter yaz · ↑↓ ile gez · Enter ile aç · Cmd+K kapat
            </Typography>
          )}
          {q.trim().length >= 2 && !loading && flatItems.length === 0 && (
            <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
              Sonuç bulunamadı
            </Typography>
          )}
          {renderGroup("Siparişler", results.orders.map(o => ({ type: "order", id: o.id, primary: `#${o.id} — ${o.user_name || "İsimsiz"}`, secondary: `${parseFloat(o.total_price || 0).toFixed(2)} ₺ · ${o.status}`, path: `/orders` })), ShoppingCartIcon, 0)}
          {renderGroup("Müşteriler", results.customers.map(c => ({ type: "customer", id: c.id, primary: c.company_name || c.name, secondary: `${c.email || c.phone || ""} ${c.tax_number ? "· VKN " + c.tax_number : ""}`, path: `/users` })), PersonIcon, results.orders.length)}
          {renderGroup("Ürünler", results.products.map(p => ({ type: "product", id: p.id, primary: p.name, secondary: `${p.sku || ""} · Stok: ${p.stock}`, path: `/products` })), InventoryIcon, results.orders.length + results.customers.length)}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
